<div
  class="select-container"
  [attr.data-size]="size()"
  [attr.data-disabled]="isComponentDisabled"
  [attr.data-error]="error()"
  [attr.data-required]="required()"
  [attr.data-open]="isOpen"
>
  @if (label()) {
    <umss-label
      [text]="label()!"
      [size]="size()"
      [required]="required()"
      [disabled]="isComponentDisabled"
      [error]="error()"
    ></umss-label>
  }

  <div
    class="select-trigger"
    [class.disabled]="isComponentDisabled"
    [class.error]="error()"
    [class.open]="isOpen"
    [class.loading]="loading().selector"
    [class.elevated]="filterStyle() === 'elevated'"
    [attr.tabindex]="isComponentDisabled ? -1 : 0"
    role="combobox"
    [attr.aria-expanded]="isOpen"
    aria-haspopup="listbox"
    [attr.aria-controls]="listboxId"
    [attr.aria-label]="label() || placeholder()"
    [attr.aria-required]="required()"
    [attr.aria-invalid]="error()"
    (click)="onToggleDropdown()"
    (keydown)="onKeyDown($event)"
  >
    <span
      class="select-value"
      [class.placeholder]="!selectedOption()"
      [class.elevated]="filterStyle() === 'elevated'"
    >
      {{ displayText() }}
    </span>

    @if (loading().selector) {
      <div class="loading-spinner">
        <umss-icon
          name="phosphorCircleNotch"
          size="16px"
          class="spinner-icon"
        ></umss-icon>
      </div>
    }

    @if (!loading().selector) {
      <span class="dropdown-indicator">
        <umss-icon name="phosphorCaretUpDown" size="16px"></umss-icon>
      </span>
    }

    @if (clearable() && (selectedOption() || selectedOptions().length > 0)) {
      <span class="dropdown-indicator">
        <umss-icon-button
          (onClick)="onClear($event)"
          iconName="phosphorX"
          variant="Ghost"
          iconColor="var(--red-500)"
          size="Small"
          iconSize="16px"
        ></umss-icon-button>
      </span>
    }
  </div>

  @if (isOpen) {
    <div
      #optionsContainer
      [id]="listboxId"
      role="listbox"
      class="select-dropdown-wrapper"
      [class.pos-top]="verticalPosition === 'top'"
      [class.pos-bottom]="verticalPosition === 'bottom'"
      [class.pos-left]="horizontalPosition === 'left'"
      [class.pos-right]="horizontalPosition === 'right'"
      [style]="dropdownStyle"
    >
      @if (searchable()) {
        <div class="search" #searchContainer>
          <umss-search-bar
            (search)="onSearch($event)"
            [defaultValue]="lastSearchTerm"
          ></umss-search-bar>
        </div>
      }
      <umss-menu
        class="select-dropdown"
        [size]="size()"
        spacing="None"
        [loading]="false"
        [disabled]="false"
      >
        @if (!loading().menu) {
          @if (hasGroups()) {
            @if (groupedOptions()['default'].length) {
              @for (
                option of groupedOptions()['default'];
                track trackByValue($index, option)
              ) {
                <umss-menu-item
                  [label]="option.label"
                  [iconName]="option.iconName"
                  [description]="option.description"
                  [shortcut]="option.shortcut"
                  [disabled]="!!option.disabled"
                  [selected]="isOptionSelected(option)"
                  [size]="size()"
                  [value]="option.value"
                  (itemClick)="onSelectOption(option)"
                >
                  @if (option.avatar) {
                    <umss-avatar
                      [src]="option.avatar.src"
                      [name]="option.avatar.name"
                      [initials]="option.avatar.initials"
                      [status]="option.avatar.status"
                      size="Small"
                    ></umss-avatar>
                  }
                  @if (option.decoration) {
                    <umss-decoration
                      [type]="option.decoration.type || 'Text'"
                      [text]="option.decoration.text"
                      [icon]="option.decoration.icon"
                      [color]="option.decoration.color"
                      [backgroundColor]="option.decoration.backgroundColor"
                      size="Small"
                      position="TopRight"
                    ></umss-decoration>
                  }
                </umss-menu-item>
              }
            }
            @for (groupName of groupNames(); track groupName) {
              <umss-menu-group-label
                [text]="groupName"
                [size]="size()"
              ></umss-menu-group-label>
              @for (
                option of groupedOptions()[groupName];
                track trackByValue($index, option)
              ) {
                <umss-menu-item
                  [label]="option.label"
                  [iconName]="option.iconName"
                  [description]="option.description"
                  [shortcut]="option.shortcut"
                  [disabled]="!!option.disabled"
                  [selected]="isOptionSelected(option)"
                  [size]="size()"
                  [value]="option.value"
                  (itemClick)="onSelectOption(option)"
                >
                  @if (option.avatar) {
                    <umss-avatar
                      [src]="option.avatar.src"
                      [name]="option.avatar.name"
                      [initials]="option.avatar.initials"
                      [status]="option.avatar.status"
                      size="Small"
                    ></umss-avatar>
                  }
                  @if (option.decoration) {
                    <umss-decoration
                      [type]="option.decoration.type || 'Text'"
                      [text]="option.decoration.text"
                      [icon]="option.decoration.icon"
                      [color]="option.decoration.color"
                      [backgroundColor]="option.decoration.backgroundColor"
                      size="Small"
                      position="TopRight"
                    ></umss-decoration>
                  }
                </umss-menu-item>
              }
            }
          } @else {
            @for (
              option of filteredOptions();
              track trackByValue($index, option)
            ) {
              <umss-menu-item
                [label]="option.label"
                [iconName]="option.iconName"
                [description]="option.description"
                [shortcut]="option.shortcut"
                [disabled]="!!option.disabled"
                [selected]="isOptionSelected(option)"
                [size]="size()"
                [value]="option.value"
                (itemClick)="onSelectOption(option)"
              >
                @if (option.avatar) {
                  <umss-avatar
                    [src]="option.avatar.src"
                    [name]="option.avatar.name"
                    [initials]="option.avatar.initials"
                    [status]="option.avatar.status"
                    size="Small"
                  ></umss-avatar>
                }
                @if (option.decoration) {
                  <umss-decoration
                    [type]="option.decoration.type || 'Badge'"
                    [text]="option.decoration.text"
                    [icon]="option.decoration.icon"
                    [color]="option.decoration.color"
                    [backgroundColor]="option.decoration.backgroundColor"
                    size="Small"
                  ></umss-decoration>
                }
              </umss-menu-item>
            }
            @if (loading().options) {
              <ul class="skeleton-option-container">
                @for (x of [0, 1, 2]; track x) {
                  <div class="animate-pulse skeleton-option"></div>
                }
              </ul>
            }
          }
          @if (filteredOptions().length === 0) {
            <umss-menu-item
              label="No options available"
              [disabled]="true"
              [size]="size()"
            ></umss-menu-item>
          }
        } @else {
          <ul class="skeleton-option-container">
            @for (x of [0, 1, 2, 3, 4, 5]; track x) {
              <div class="animate-pulse skeleton-option"></div>
            }
          </ul>
        }
      </umss-menu>

      @if (helperText() && !error()) {
        <div class="helper-text">{{ helperText() }}</div>
      }
      @if (error() && errorMessage()) {
        <div class="error-message">{{ errorMessage() }}</div>
      }
    </div>
  }
</div>
